/**
 * The files an operator adds to a New revision (JOBS-UX-SPEC 3.1 Files,
 * 3.3 step 2, decision D11).
 *
 * Emails (.eml, .msg) and text files are read in the browser and travel in
 * the Revise request as text; the server writes them into the quote's
 * Reference Files in the same transaction as the revision. PDFs and photos
 * stay as File objects and are added after the reload through the ordinary
 * Stage 5 reference-file path, tagged with the new Rev.
 *
 * Any file that cannot be read stops the revision before anything is sent:
 * the operator is told which file, and nothing changes on the server.
 *
 * The limits match the server's validation of the request (JOBS-UX-SPEC
 * 6.4): at most 10 files, each 20 MB or less, text content 1 to 200,000
 * characters, 1,000,000 characters in all.
 */

import { emailToPlainSummary, parseEmlBuffer } from '../../utils/eml-parser';
import { parseMsgBinary } from '../../utils/msg-parser';

export type RevisionFileKind = 'email' | 'text' | 'pdf' | 'photo';

/** One text attachment as the Revise request carries it. */
export interface RevisionAttachment {
    name: string;
    kind: 'email' | 'text';
    content: string;
}

export const REVISION_FILE_LIMIT = 10;
export const REVISION_FILE_BYTES_MAX = 20 * 1024 * 1024;
export const REVISION_ATTACHMENT_CONTENT_MAX = 200_000;
export const REVISION_ATTACHMENTS_TOTAL_MAX = 1_000_000;
/** What the file picker offers. */
export const REVISION_FILE_ACCEPT = '.eml,.msg,.txt,.pdf,.jpg,.jpeg,.png,.webp,.heic';

const KIND_BY_EXTENSION: Record<string, RevisionFileKind> = {
    eml: 'email',
    msg: 'email',
    txt: 'text',
    pdf: 'pdf',
    jpg: 'photo',
    jpeg: 'photo',
    png: 'photo',
    webp: 'photo',
    heic: 'photo',
};

function extensionOf(name: string): string {
    const match = String(name || '').match(/\.([a-z0-9]+)$/i);
    return match ? match[1]!.toLowerCase() : '';
}

/** What a file is to the New revision sheet, by its extension, or null when the sheet does not take it. */
export function revisionFileKind(name: string): RevisionFileKind | null {
    return KIND_BY_EXTENSION[extensionOf(name)] ?? null;
}

function fileName(file: File): string {
    return String(file?.name || '').trim() || 'This file';
}

/**
 * Split newly picked files into the ones the sheet keeps and a sentence for
 * each one it does not. `current` is what the sheet already holds.
 */
export function checkRevisionFiles(current: readonly File[], picked: readonly File[]): { accepted: File[]; refusals: string[] } {
    const accepted: File[] = [];
    const refusals: string[] = [];
    let count = current.length;
    for (const file of picked) {
        const name = fileName(file);
        if (!revisionFileKind(name)) {
            refusals.push(`${name} was not added. Word files can be added from Stage 1.`);
        } else if (Number(file.size) > REVISION_FILE_BYTES_MAX) {
            refusals.push(`${name} was not added. Each file must be 20 MB or smaller.`);
        } else if (count >= REVISION_FILE_LIMIT) {
            refusals.push(`${name} was not added. Up to ${REVISION_FILE_LIMIT} files can go with one revision.`);
        } else {
            accepted.push(file);
            count += 1;
        }
    }
    return { accepted, refusals };
}

async function readBuffer(file: File): Promise<ArrayBuffer> {
    if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
    return new Response(file).arrayBuffer();
}

function withLf(text: string): string {
    return text.replace(/\r\n?/g, '\n');
}

function msgToText(buffer: ArrayBuffer): string {
    // The same header block the Stage 1 preprocessing gives an Outlook email
    // (services/preprocessing-pipeline.js), so the AI reads both alike.
    const parsed = parseMsgBinary(buffer);
    const headers = (parsed?.headers || {}) as Record<string, string | undefined>;
    const lines: string[] = [];
    if (headers.emailFrom) lines.push(`From: ${headers.emailFrom}`);
    if (headers.emailTo) lines.push(`To: ${headers.emailTo}`);
    if (headers.emailCc) lines.push(`CC: ${headers.emailCc}`);
    if (headers.emailDate) lines.push(`Date: ${headers.emailDate}`);
    if (headers.emailSubject) lines.push(`Subject: ${headers.emailSubject}`);
    const body = String(parsed?.body || '');
    return lines.length > 0 ? `${lines.join('\n')}\n\n${body}` : body;
}

async function readAsText(file: File, kind: 'email' | 'text'): Promise<string> {
    const name = fileName(file);
    if (kind === 'text') return typeof file.text === 'function' ? file.text() : new TextDecoder().decode(await readBuffer(file));
    const buffer = await readBuffer(file);
    if (extensionOf(name) === 'msg') return msgToText(buffer);
    return emailToPlainSummary(parseEmlBuffer(new Uint8Array(buffer)));
}

export type PreparedRevisionAttachments =
    | { ok: true; attachments: RevisionAttachment[]; otherFiles: File[]; otherFileNames: string[] }
    | { ok: false; problem: string };

/**
 * Read the picked files for the Revise request. Emails and text become text
 * attachments; PDFs and photos are returned for the upload after the reload,
 * and named in the request so the reason file can list them.
 */
export async function prepareRevisionAttachments(files: readonly File[] | null | undefined): Promise<PreparedRevisionAttachments> {
    const list = Array.isArray(files) ? files : [];
    const { refusals } = checkRevisionFiles([], list);
    if (refusals.length > 0) return { ok: false, problem: refusals[0]! };
    const attachments: RevisionAttachment[] = [];
    const otherFiles: File[] = [];
    let total = 0;
    for (const file of list) {
        const name = fileName(file);
        const kind = revisionFileKind(name);
        if (kind === 'pdf' || kind === 'photo') {
            otherFiles.push(file);
            continue;
        }
        if (kind !== 'email' && kind !== 'text') continue;
        let content = '';
        try {
            content = withLf(await readAsText(file, kind)).trim();
        } catch {
            content = '';
        }
        if (!content) return { ok: false, problem: `${name} could not be read. Nothing was changed.` };
        if (content.length > REVISION_ATTACHMENT_CONTENT_MAX) {
            return {
                ok: false,
                problem: `${name} is too long to add as text (more than ${REVISION_ATTACHMENT_CONTENT_MAX.toLocaleString('en-AU')} characters). Nothing was changed.`,
            };
        }
        total += content.length;
        if (total > REVISION_ATTACHMENTS_TOTAL_MAX) {
            return {
                ok: false,
                problem: `The emails and text files are too long together (more than ${REVISION_ATTACHMENTS_TOTAL_MAX.toLocaleString('en-AU')} characters). Add ${name} from Reference Files after the revision. Nothing was changed.`,
            };
        }
        attachments.push({ name, kind, content });
    }
    return { ok: true, attachments, otherFiles, otherFileNames: otherFiles.map(fileName) };
}
